import React from "react";
import * as m from "../../lib/model";
import { Logo } from "../shared";
import { ROOM_SECTIONS } from "../ShareRoomModal";
import { FinancialsTab, SourcesUsesTab, ReturnsTab, SensitivityTab, MarketTab, RisksTab } from "./RoomTabs";
import { OpportunityTab, ThesisTab, PipelineTab, TeamTab, StructureTab } from "./Sections";
import { RollUpTab } from "./RollUpTab";
import { QsbsBlock, qsbsBump } from "./Qsbs";

// Interactive-only tabs (data room, Q&A, invest, pass) never go on paper.
export const PRINTABLE = ["overview", "thesis", "market", "returns", "sourcesuses", "financials", "qsbs", "sensitivity", "assumptions", "pipeline", "team", "rollup", "risks"];
// The after-tax page is paper-only: in the room the QSBS block sits inside the Opportunity tab.
const PAPER_ONLY = [{ id: "qsbs", label: "QSBS & after-tax LP economics", group: "The numbers",
  desc: "§1202 exclusion, after-tax proceeds at $500k, $1M and the entire equity check" }];
export const PRINTABLE_SECTIONS = PRINTABLE
  .map((id) => [...ROOM_SECTIONS, ...PAPER_ONLY].find((s) => s.id === id))
  .filter(Boolean);
export const LENDER_DEFAULT = ["overview", "sourcesuses", "financials", "assumptions", "rollup", "team", "risks"];
export const INVESTOR_DEFAULT = PRINTABLE.filter((id) => id !== "sensitivity");

const coverStats = (M, ask, audience) => {
  const cov = m.coverage(M);
  if (audience === "lender") {
    const ex = m.exitReturns(M);
    return [["Senior debt", m.fmt$(m.loanAmt(M))], ["Rate · term", `${M.rate}% · ${M.loanYrs} yrs`],
      [`DSCR Y${cov.year}`, m.fmtX(cov.dscr)], ["Sponsor equity", m.fmt$(ex.equityInvested)]];
  }
  const wf = m.lpWaterfall(M);
  // The cover tiles stay as clean headline numbers — the QSBS note lives on the returns page.
  return [["The ask", m.fmt$(ask)], ["LP MOIC", m.fmtX(wf.lpMoic)],
    ["LP IRR", isNaN(wf.lpIrr) ? "n/a" : m.fmtPct(wf.lpIrr)], [`DSCR Y${cov.year}`, m.fmtX(cov.dscr)]];
};

// Cover + one page per section, rendered from the exact same components the investor room uses.
// Shared by the room's own "Save as PDF" (PrintDeck) and the Teaser Generator's package builder.
export function DeckPages({ data, M, title, platform, market, ask, investor, audience = "investor", coverPhoto, onPage }) {
  const firm = (data.snapshot && data.snapshot.firm) || null;
  const own = (data.snapshot && data.snapshot.ownPlatform) || null;
  const thesis = data.thesis || {};
  // Deals the sponsor has marked non-QSBS drop the page rather than print an empty one.
  const sections = PRINTABLE_SECTIONS.filter((s) => data.sections.includes(s.id)
    && !(s.id === "qsbs" && (thesis.qsbsEligible === false || audience === "lender")));
  const date = new Date().toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" });
  const kind = audience === "lender" ? "Confidential credit package" : "Confidential investment memorandum";

  const render = (id) => {
    switch (id) {
      case "overview": return <OpportunityTab M={M} title={title} platform={platform} market={market} thesis={thesis} ask={ask} showInvest={false} minCommit={data.minCommit || 25000} classes={data.classes || []} timeline={data.timeline || []} firm={firm} own={own} />;
      case "thesis": return <ThesisTab M={M} thesis={thesis} platform={platform} own={own} />;
      case "market": return <MarketTab market={market} thesis={thesis} />;
      case "financials": return <FinancialsTab M={M} />;
      case "sourcesuses": return <SourcesUsesTab M={M} />;
      case "returns": return <ReturnsTab M={M} />;
      case "sensitivity": return <SensitivityTab M={M} />;
      case "assumptions": return <StructureTab M={M} blind={!!data.blind} classes={data.classes || []} timeline={data.timeline || []} ask={ask} thesis={thesis} firm={firm} />;
      case "pipeline": return <PipelineTab M={M} pipeline={data.pipeline || []} />;
      case "team": return <TeamTab team={(data.team && data.team.length ? data.team : (firm && firm.team) || [])} firm={firm} M={M} />;
      case "qsbs": return <QsbsBlock M={M} minCommit={data.minCommit || 25000} note={thesis.qsbsNote} ask={ask} deck />;
      case "rollup": return <RollUpTab M={M} />;
      case "risks": return <RisksTab M={M} />;
      default: return null;
    }
  };

  return (
    <>
      <section className="print-page print-cover" data-testid="print-cover" ref={onPage}>
        {coverPhoto ? (() => {
          const shots = String(coverPhoto).split("|").filter(Boolean);
          // One photo prints as-is; two or three cross-fade on screen (the first is what the PDF captures).
          return shots.length > 1
            ? <div className="print-cover-fade" data-testid="print-cover-photo">
              {shots.map((src, i) => (
                <img key={i} src={src} alt="" style={{ animationDelay: `${i * (6 / shots.length)}s`, animationDuration: `${6}s` }} />
              ))}
            </div>
            : <img className="print-cover-photo" src={shots[0]} alt="" data-testid="print-cover-photo" />;
        })() : null}
        <Logo height={72} testid="print-logo" />
        <span className="ir-eyebrow">{firm?.name || "XYZ Ventures"} · {kind}</span>
        <h1>{title}</h1>
        {thesis.headline && <p className="print-cover-line">{thesis.headline}</p>}
        <div className="print-cover-stats">
          {coverStats(M, ask, audience).map(([k, v]) => <div key={k}><span>{k}</span><b>{v}</b></div>)}
        </div>
        <div className="print-cover-foot">Prepared for {investor || "the named recipient"} · {date}<br />Illustrative projections; not an offer to sell securities. Do not distribute without the sponsor's consent.</div>
      </section>
      {sections.map((s, i) => (
        <section className="print-page" key={s.id} data-testid={`print-section-${s.id}`} ref={onPage}>
          <div className="print-page-head">
            <span>{String(i + 1).padStart(2, "0")} · {s.label}</span>
            <span>Section {i + 1} of {sections.length} · {title} · {firm?.name || "XYZ Ventures"}</span>
          </div>
          <div className="ir-body print-body">{render(s.id)}</div>
        </section>
      ))}
    </>
  );
}
